/**
 * GEX-2 LIVE 0DTE PANE — the pure half. R424(2), owner order 19:2xZ.
 *
 * ⛔ ENGINE CHOICE, REVERSED WITH A REASON (stack-hand, `4398c32`). At `81a72b3b` this pane was ruled
 * the LWC conversion's first customer. **That ruling assumed R423's tomorrow.** The owner moved it to
 * tonight, and the condition it bound itself to — *two engines are acceptable, two design systems are
 * not* — then cut the other way. The pane reuses the SERVED ladder's language, so it is born
 * consistent. LWC remains the conversion's path and this is not evidence against it.
 * ⇒ ***A DESIGN DECISION MADE FOR TOMORROW IS NOT AUTOMATICALLY THE RIGHT ONE FOR TONIGHT.***
 *
 * ⚠⚠ THE CONTRACT IS NO LONGER GUESSED — AND IT DID NOT MATCH THE GUESS.
 * The parked pane was written against the contract R424(1) DESCRIBED, in the honest absence of a
 * served route. ar landed `@c54af27` and three field names differ from the guess:
 * ```
 *   guessed        served              consequence had it shipped unchecked
 *   rows           strikes             feed.rows undefined -> `.length` throws -> pane dies
 *   spot_basis     spot_provenance     the R422 disclosure silently falls back to the default
 *   age_s          (not served)        `age_s != null` false -> NO chip at all, reads as fine
 * ```
 * ⇒ ⭐⭐ ***A PANE WRITTEN AGAINST A DESCRIBED CONTRACT IS A HYPOTHESIS, AND THE PART THAT ROTS
 *   SILENTLY IS THE OPTIONAL FIELD.*** `rows` fails loudly the first time it renders. `age_s` does
 *   not fail at all — it renders a pane with no freshness chip, which is exactly what a healthy
 *   pane looks like. **The mismatch that hurts is the one whose absence is indistinguishable from
 *   health.** That is why `ageVerdict` below refuses to be silent about an unserved age.
 */

/** One strike row, structurally as `/api/dealer/ladder` lands it (`gexLadder.ts::LadderStrike`). */
import { ladderSpotPlacement, type LadderSpot } from "./layout.ts";
import type { LiveMarker } from "./livePriceMarker.ts";
import type { VenueStateLike } from "./session.ts";

export interface Gex2Row {
  strike: number;
  net: number;
  /** MINUTE DELTA. null = the strike was not in BOTH snapshots — no change was MEASURED. */
  delta: number | null;
  delta_basis: "paired" | "coverage_added" | "no_previous_snapshot";
  /** THIS ROW's vendor time. Per-row, never the snapshot's. */
  time: string | null;
  /** Server-computed against the snapshot's OWN newest vendor time, not against wall clock. */
  stale?: boolean;
}

/**
 * The pane's structural view of the ladder response.
 *
 * ⚠ DECLARED HERE AND NOT IMPORTED FROM `api.ts` ON PURPOSE — `api.ts` carries a TS parameter
 * property, so Node's strip-only test runner cannot import it at all, and a predicate parked there
 * would be UNARMABLE. `api.ts::DealerLadderResponse` is the wire authority; this is the subset the
 * pure lane reads, and TS checks one against the other at the call site in the panel.
 */
export interface Gex2Feed {
  ok: boolean;
  reason?: string;
  spot: number;
  expiry: string;
  ticker?: string;
  strikes: Gex2Row[];
  spot_provenance?: string;
  coverage_added?: number;
  coverage_dropped?: number;
  prev_comparable?: boolean;
  prev_incomparable_reason?: string | null;
  newest_vendor_time?: string | null;
  scrub_floor_ts?: string;
  scrub_floor_basis?: string;
  ts_applied?: string | null;
  /**
   * ⛔ SERVER-SIDE, on the OLDEST near-spot row (ar `@d559fc2`). Absent still means UNKNOWN, and
   * the chip still says so — see `ageVerdict`, which pins the SPEC and not tonight's payload.
   */
  age_s?: number | null;
  oldest_vendor_time?: string | null;
  /** The ROUTE's budget (`LADDER_BUDGET_S` 390, ruled). The pane never keeps a second one. */
  budget_s?: number;
  /** The route's own verdict. Preferred over any local recomputation. */
  stale?: boolean;
  age_basis?: string;
  venue_state?: VenueStateLike;
}

/** What a strike's minute delta means for the reader. `null` delta is UNKNOWN, never "flat". */
export type DeltaKind = "add" | "pull" | "flat" | "unknown" | "closed";

/**
 * ⛔ `null` IS NOT ZERO AND THIS IS THE WHOLE POINT OF THE GLYPH.
 *
 * A null delta means there was no prior snapshot to diff — we did not measure a change. Rendering
 * that as "flat" would assert dealers held steady, which is a claim nobody made. The estate has paid
 * for this exact confusion twice today (`press_v1` NULL vs 0; `v: null` on the forming bar).
 */
export function deltaKind(delta: number | null, epsilon = 0): DeltaKind {
  if (delta === null || !Number.isFinite(delta)) return "unknown";
  if (Math.abs(delta) <= epsilon) return "flat";
  return delta > 0 ? "add" : "pull";
}

/**
 * The row's delta state ON THE FACE, given the venue.
 *
 * ⛔ AT A CLOSED VENUE EVERY DELTA IS A DIFF OF TWO POST-CLOSE SNAPSHOTS OF ONE FROZEN BOOK.
 * Measured by HUDCHAT-38 at the worker origin: `/api/dealer/ladder?ts=2026-09-22T22:59:00Z`
 * served 81 rows with 81 deltas of exactly 0, so every row drew `flat` - the dashes AUDIT-1 saw
 * (R2626 item 5) - asserting "dealers held steady" about a market that was shut. A minute delta
 * describes a live book; while CLOSED the ladder draws the last session's exposure and no mark.
 */
export function rowDeltaKind(delta: number | null, venueClosed: boolean): DeltaKind {
  return venueClosed ? "closed" : deltaKind(delta);
}

/**
 * The MARK each delta state gets in the glyph column — and `null` means "draw nothing, deliberately".
 *
 * ⛔⛔ THIS EXISTS BECAUSE THE SVG BRANCHED ON TWO OF FOUR STATES AND NO ARM COULD SEE IT.
 * GK-tt measured it (`bh=dbb961df`): `deltaKind` returns FOUR states, the template had
 * `{#if add}▲{:else if pull}▼{/if}` — so **`flat` and `unknown` both rendered as nothing**, the pure
 * lane's distinction discarded at the last layer.
 * ⇒ ⭐⭐ ***THE PURE LAYER WAS RIGHT AND ARMED; WHAT THE OWNER READ WAS NEITHER.*** Moving the mapping
 * here is the cure, not the dash: **a decision inside markup is unreachable by every arm this repo
 * has**, so it drifts silently. Now the template renders `glyphMark(k)` and the mapping is armable.
 * ⚠ `unknown` KEEPS its blank — that was always deliberate (a null delta is *no prior snapshot*, so a
 * mark would assert a measurement nobody took). `flat` was never decided at all; it just had no arm.
 */
export function glyphMark(kind: DeltaKind): string | null {
  switch (kind) {
    case "add": return "▲";
    case "pull": return "▼";
    case "flat": return "–";
    // CLOSED is a STATE, not a measurement of dealers holding still (R2689 item 3). Declared in
    // the legend, like `unknown`, so the empty column reads as stated rather than missing.
    case "closed": return null;
    // ⛔ NOT A MARK, AND NOT AN OVERSIGHT: absence of measurement is the one state that must not
    // draw. It is DECLARED in the legend instead, so the blank reads as stated rather than missing.
    case "unknown": return null;
  }
}

/**
 * The pane's own refusal, mirroring the chart's: a feed that cannot be drawn says WHY.
 * Returns null when the ladder is drawable.
 */
export function gex2BlockedReason(feed: Gex2Feed | null, error: string | null): string | null {
  if (error) return `0DTE ladder unavailable — ${error}`;
  if (!feed) return null; // still loading; the chip says so
  if (!feed.ok) return feed.reason ?? "the ladder route returned not-ok";
  // ⛔ A SERVED-BUT-SHAPELESS PAYLOAD IS A FAULT, NOT AN EMPTY LADDER. `strikes` was `rows` in the
  // guessed contract; if a future rename lands, this says so instead of throwing on `.length`.
  if (!Array.isArray(feed.strikes)) return "the ladder route served no `strikes` array — the contract moved";
  if (feed.strikes.length === 0) return "the 0DTE chain served no strikes near spot";
  return null;
}

/**
 * ⚠ THE PANE MUST SAY THAT `spot` IS UNSPECCED, BECAUSE THE WHOLE LADDER IS CENTRED ON IT.
 *
 * R424(3): the pane ships with spot LABELED, not blocked. Three hands proved today that a stale spot
 * does not merely misplace a line — it NARROWS THE CANDIDATE SET and a ruled function then picks a
 * different strike. A ladder centred on a spot whose provenance is pending must disclose that, or it
 * inherits the same silent confidence the chart just shed.
 */
export function spotProvenanceNote(feed: Gex2Feed | null): string | null {
  if (!feed?.ok) return null;
  return feed.spot_provenance ?? "spot provenance pending spec";
}

/** What the freshness chip may claim. UNSERVED is a state of OUR KNOWLEDGE, not of the feed. */
export type AgeVerdict =
  | { kind: "live"; ageS: number; budgetS: number }
  | { kind: "stale"; ageS: number; budgetS: number }
  | { kind: "unserved"; note: string };

/**
 * ⛔⛔ AN UNSERVED AGE READS AS A HEALTHY PANE, AND THAT IS WHY IT GETS A CHIP OF ITS OWN.
 *
 * The parked pane wrote `{#if feed.age_s != null}`, so against a payload serving no `age_s` the
 * freshness chip simply **would not render** — and a pane with no STALE chip is indistinguishable
 * from a fresh one. ⇒ ***THE ABSENCE OF A WARNING MUST NOT RENDER AS THE ABSENCE OF A PROBLEM.***
 *
 * ⚠ THIS BRANCH SURVIVES ar's CURE ON PURPOSE. `@d559fc2` now serves `age_s`, so the `unserved`
 * arm no longer fires on the live route. It stays because **the arm pins the SPEC, not tonight's
 * payload** — the rule is *an age we cannot measure is never drawn as health*, and that rule outlives
 * the commit that happened to satisfy it. Deleting it the day it goes quiet is how a cured defect
 * walks back in through a later refactor.
 *
 * ⛔ THE BUDGET COMES FROM THE PAYLOAD. ar serves `budget_s` = the ruled `LADDER_BUDGET_S` (390).
 * `fallbackBudgetS` is used ONLY when the route sends none — a component-local default that could
 * silently outvote the route would be a second budget for one instrument.
 * ⚠ And when the route states its own `stale` verdict, that verdict WINS: recomputing it here would
 * put two answers to one question on the same screen, which is the disagreement the reader cannot
 * adjudicate.
 */
export function ageVerdict(feed: Gex2Feed | null, fallbackBudgetS: number): AgeVerdict | null {
  if (!feed?.ok) return null;
  const age = feed.age_s;
  if (age === undefined || age === null || !Number.isFinite(age)) {
    return {
      kind: "unserved",
      note: "the ladder route served no age_s; a browser-clock age would be a confound, not a measurement",
    };
  }
  const budgetS = typeof feed.budget_s === "number" && Number.isFinite(feed.budget_s)
    ? feed.budget_s
    : fallbackBudgetS;
  const stale = typeof feed.stale === "boolean" ? feed.stale : age > budgetS;
  return stale ? { kind: "stale", ageS: age, budgetS } : { kind: "live", ageS: age, budgetS };
}

/**
 * ⛔ COVERAGE IS NOT FLOW — AND SAYING SO IS THE PANE'S HALF OF ar's CURE.
 *
 * ar's route refuses to diff strikes absent from either snapshot, so the 90-strike pagination event
 * at 18:05:15Z can no longer paint phantom adds. But the route can only make the number HONEST; it
 * cannot make the reader AWARE. A ladder whose glyph column suddenly thins out, with no explanation,
 * reads as dealers going quiet. ⇒ when coverage moved, the pane SAYS the chain changed size.
 */
export function coverageNote(feed: Gex2Feed | null): string | null {
  if (!feed?.ok) return null;
  const added = feed.coverage_added ?? 0;
  const dropped = feed.coverage_dropped ?? 0;
  if (added === 0 && dropped === 0) return null;
  const parts: string[] = [];
  if (added > 0) parts.push(`${added} strike${added === 1 ? "" : "s"} entered coverage`);
  if (dropped > 0) parts.push(`${dropped} left`);
  return `chain coverage changed — ${parts.join(", ")}; those strikes carry no delta (coverage is not flow)`;
}

/**
 * Why no deltas are drawn at all. The route degrades to `no_previous_snapshot` LOUDLY when the
 * previous snapshot is a different root or expiry; an undrawn glyph column with no reason reads as
 * a market that stopped moving.
 */
export function comparisonNote(feed: Gex2Feed | null): string | null {
  if (!feed?.ok) return null;
  if (feed.prev_comparable === false) {
    return `no minute delta — ${feed.prev_incomparable_reason ?? "the previous snapshot is not comparable"}`;
  }
  return null;
}

/**
 * ⛔ IS THE PANE'S "0DTE" CHIP TELLING THE TRUTH? ASK THE PAYLOAD, NEVER THE HEADING.
 *
 * The parked pane hard-codes a `0DTE` chip and prints the served expiry beside it. Those are two
 * independent claims and nothing checked that they agree. ap measured that every snapshot before
 * 06:34:21Z carries expiry **2031-12-19** — so a scrub below the floor, or any future substrate
 * change, renders LEAPs under a chip that says 0DTE. ar's route refuses the scrub; this refuses the
 * LABEL, which is the half that survives a route change.
 * ⇒ ***A LABEL THAT CANNOT BE CONTRADICTED BY ITS OWN DATA IS DECORATION.***
 *
 * `todayUtc` is injected (never `new Date()` inside) so the arm can pin the spec at a fixed instant.
 */
export function dteLabel(feed: Gex2Feed | null, todayUtc: string): { text: string; honest: boolean } {
  if (!feed?.ok || !feed.expiry) return { text: "0DTE", honest: true };
  const honest = feed.expiry === todayUtc;
  return honest ? { text: "0DTE", honest: true } : { text: `NOT 0DTE · ${feed.expiry}`, honest: false };
}

/**
 * The scrubber's floor, as epoch ms. Below it ar's route answers **400**, so the control must not be
 * able to ask — a UI that can request a guaranteed refusal is a UI that teaches the reader the pane
 * is broken.
 */
/**
 * ⛔ R591(3), THE OWNER'S ASK: *"spot lives in the prose line (7645) — the owner cannot find it.
 * Render spot LARGE at the ladder's head, with its age."*
 *
 * The head is a READING, never a blank. `unserved` is a state with a stated reason, because a
 * missing spot is the one absence this pane cannot afford to render quietly: the whole ladder is
 * CENTRED on spot, so a spot we do not have does not merely misplace a line — it narrows the
 * candidate set and a ruled function then picks a different strike (R424(3), and three hands proved
 * it live).
 *
 * ⚠ AND THE HONESTY LINE I WILL NOT CROSS TO SATISFY THE ASK: the desk said "with its age", and
 * **this route serves no age FOR SPOT.** `age_s` is measured server-side on the OLDEST NEAR-SPOT
 * ROW (`age_basis`), which is the SNAPSHOT's freshness, not the spot tick's. Rendering it flush
 * against a big spot number would let it read as "this price is 37s old", which is a claim the
 * payload does not make. ⇒ ***THE HEAD SHOWS THE SNAPSHOT'S AGE, LABELLED AS THE SNAPSHOT'S.***
 * A number borrowed from a neighbouring field and shown under a new name is the quietest way to
 * invent a measurement.
 */
export type SpotHead =
  | { kind: "served"; text: string; spot: number }
  | { kind: "unserved"; note: string };

export function spotHead(feed: Gex2Feed | null): SpotHead {
  if (!feed) return { kind: "unserved", note: "not loaded yet" };
  if (!feed.ok) return { kind: "unserved", note: feed.reason ?? "the ladder route returned not-ok" };
  const spot = feed.spot;
  if (typeof spot !== "number" || !Number.isFinite(spot) || spot <= 0) {
    // ⛔ NOT "0". A non-positive or absent spot is UNKNOWN, and 0 is a price.
    return { kind: "unserved", note: "the route served no usable spot" };
  }
  // Trailing zeros trimmed so 7650 reads 7650, while 7650.25 keeps the quarter that matters.
  return { kind: "served", spot, text: String(Number(spot.toFixed(2))) };
}

export function scrubFloorMs(feed: Gex2Feed | null): number | null {
  const ts = feed?.scrub_floor_ts;
  if (!ts) return null;
  const ms = Date.parse(ts);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Clamp a scrub request into the range the route will actually serve.
 *
 * ⚠ RETURNS `null` FOR "LIVE", AND LIVE IS NOT THE SAME AS "THE NEWEST POSITION ON THE SLIDER".
 * The newest snapshot MOVES; a slider parked at max would silently pin the reader to whatever
 * instant they last released it on, while the pane kept saying it was live.
 */
export function clampScrub(askedMs: number | null, floorMs: number | null, nowMs: number): number | null {
  if (askedMs === null) return null;
  if (!Number.isFinite(askedMs)) return null;
  if (askedMs >= nowMs) return null; // at or past the head IS live
  if (floorMs !== null && askedMs < floorMs) return floorMs;
  return askedMs;
}

/**
 * THE SPOT RULE — ONE CALL, TWO OUTPUTS, AND THAT IS THE WHOLE POINT.
 *
 * ⛔⛔ GK-as's FIX-FIRST, CONCURRED BY GK-au, ON MY OWN FIRST CUT. The panel derived the head from
 * `spotHead(feed)` and the line from `ladderSpotPlacement(feed?.spot, …)` — TWO independent reads of
 * one feed. Their mutant nulled the LINE's argument and left the import and the call untouched:
 * ```
 *   ladderSpotPlacement(feed?.spot, …) -> (null, …)
 *   test:all 49/49 · svelte-check 0 errors   ** SURVIVED **
 * ```
 * My `L7e` asserted the helper was imported, CALLED, and branched on. All three clauses hold under
 * that mutant. => ***AN IMPORT-AND-CALL CHECK IS A PRESENCE CHECK; THE FEATURE IS THE VALUE.***
 * (au, sharper: *"I drove the mutant my assertion was shaped to catch. A regex arm and a mutant
 * written by the same reading agree with each other and prove nothing."*)
 *
 * ⚠ AND THE CONSEQUENCE WAS NOT COSMETIC: under that mutant the HEAD renders the real spot while the
 * LINE renders absent — permanently, every gate green. That is the owner's original complaint
 * restored (*"the spot price isn't on the 0DTE exposure panel"*) and R808③'s ruling broken:
 * ***THE LINE AND THE HEAD MUST BE ONE NUMBER.***
 *
 * 🔑 THE CURE IS NOT A SHARPER REGEX — IT IS TO MAKE THE TWO IMPOSSIBLE TO DIVERGE. The placement is
 * derived from the HEAD'S OWN `spot`, never a second read of the feed, and both leave here in one
 * object. A call site can no longer hand the line something the head disagrees with, and the only
 * remaining component-level mutant (nulling `feed` itself) blanks the big spot number too — a
 * user-visible break instead of a silent one.
 */
export function gex2SpotRule(
  feed: Gex2Feed | null,
  displayStrikes: readonly number[],
  geom: { padT: number; rowH: number },
): { head: SpotHead; placement: LadderSpot } {
  const head = spotHead(feed);
  // ⛔ THE HEAD'S OWN NUMBER, never `feed.spot` again. One source, one number.
  const value = head.kind === "served" ? head.spot : null;
  return { head, placement: ladderSpotPlacement(value, displayStrikes, geom) };
}

/** LIVE uses the exact ladder placement function as snapshot SPOT, so equal prices mean equal y. */
export function gex2LiveRule(
  marker: LiveMarker,
  displayStrikes: readonly number[],
  geom: { padT: number; rowH: number },
): LadderSpot {
  if (marker.kind === "absent") return { kind: "absent", reason: marker.reason };
  return ladderSpotPlacement(marker.price, displayStrikes, geom);
}

/** The independent LIVE truth uses the same guarded orientation without re-reading snapshot spot. */
export function gex2LivePlacement(
  livePrice: number | null,
  displayStrikes: readonly number[],
  geom: { padT: number; rowH: number },
): LadderSpot {
  return ladderSpotPlacement(livePrice, displayStrikes, geom);
}
